import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Loader2,
    Plus,
    Edit,
    Trash2,
    DollarSign,
    FileText,
    ToggleLeft,
    ToggleRight,
    AlertCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const CaseTypes = () => {
    const [caseTypes, setCaseTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingType, setEditingType] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        price: "",
        is_active: true,
    });

    useEffect(() => {
        fetchCaseTypes();
    }, []);

    const fetchCaseTypes = async () => {
        const token = localStorage.getItem("auth_token");
        if (!token) {
            setError("Authentication required.");
            setLoading(false);
            return;
        }

        try {
            const response = await api.get("/case-types?active_only=true", {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.success) {
                setCaseTypes(response.data.data || []);
            } else {
                setError("No case types found.");
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to load case types.";
            setError(msg);
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: msg,
                confirmButtonText: "OK",
                confirmButtonColor: "#1e40af",
            });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({ name: "", description: "", price: "", is_active: true });
        setEditingType(null);
    };

    const openAddModal = () => {
        resetForm();
        setShowAddModal(true);
    };

    const openEditModal = (type) => {
        setEditingType(type);
        setFormData({
            name: type.name,
            description: type.description || "",
            price: type.price,
            is_active: type.is_active,
        });
        setShowEditModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const payload = {
            name: formData.name,
            description: formData.description,
            price: parseFloat(formData.price).toFixed(2),
            is_active: formData.is_active,
        };

        try {
            let response;
            if (editingType) {
                response = await api.put(`/case-types/${editingType.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                response = await api.post("/case-types", payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            if (response.data.success) {
                const newType = response.data.data;
                if (editingType) {
                    setCaseTypes((prev) => prev.map((t) => (t.id === editingType.id ? newType : t)));
                    setShowEditModal(false);
                    await Swal.fire({
                        icon: "success",
                        title: "Updated!",
                        text: `"${newType.name}" has been updated successfully.`,
                        confirmButtonText: "OK",
                        confirmButtonColor: "#10b981",
                    });
                } else {
                    if (newType.is_active) {
                        setCaseTypes((prev) => [...prev, newType]);
                    }
                    setShowAddModal(false);
                    await Swal.fire({
                        icon: "success",
                        title: "Added!",
                        text: `"${newType.name}" has been added successfully.`,
                        confirmButtonText: "OK",
                        confirmButtonColor: "#10b981",
                    });
                }
                resetForm();
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Operation failed.";
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: msg,
                confirmButtonText: "OK",
                confirmButtonColor: "#ef4444",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Delete Case Type?",
            html: `Are you sure you want to permanently delete <strong>"${name}"</strong>?`,
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        const token = localStorage.getItem("auth_token");
        try {
            await api.delete(`/case-types/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setCaseTypes((prev) => prev.filter((t) => t.id !== id));
            await Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: `"${name}" has been removed.`,
                confirmButtonText: "OK",
                confirmButtonColor: "#10b981",
            });
        } catch (err) {
            await Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text: err.response?.data?.message || "Could not delete case type.",
                confirmButtonText: "OK",
                confirmButtonColor: "#ef4444",
            });
        }
    };

    const toggleActive = async (id, current) => {
        const token = localStorage.getItem("auth_token");
        try {
            const response = await api.put(
                `/case-types/${id}`,
                { is_active: !current },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setCaseTypes((prev) =>
                    prev.map((t) => (t.id === id ? { ...t, is_active: !current } : t))
                );

                await Swal.fire({
                    icon: "success",
                    title: current ? "Deactivated" : "Activated",
                    text: `Status is now: ${!current ? "Active" : "Inactive"}`,
                    confirmButtonText: "OK",
                    confirmButtonColor: "#1e40af",
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } catch (err) {
            await Swal.fire({
                icon: "error",
                title: "Update Failed",
                text: "Could not change status.",
                confirmButtonText: "OK",
                confirmButtonColor: "#ef4444",
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <p className="text-red-700 font-medium">{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Case Types</h2>
                    <p className="text-gray-600 mt-1">Manage your legal service packages</p>
                </div>
                <motion.button
                    onClick={openAddModal}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <Plus className="w-4 h-4" />
                    Add Case Type
                </motion.button>
            </div>

            {/* List */}
            {caseTypes.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
                    <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <FileText className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium">No case types yet</p>
                    <p className="text-gray-500 text-sm mt-1">Click "Add Case Type" to create your first service</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {caseTypes.map((type, i) => (
                        <motion.div
                            key={type.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <h3 className="font-semibold text-gray-900 text-lg">{type.name}</h3>
                                <button
                                    onClick={() => toggleActive(type.id, type.is_active)}
                                    className="text-gray-500 hover:text-blue-600"
                                    title={type.is_active ? "Deactivate" : "Activate"}
                                >
                                    {type.is_active ? (
                                        <ToggleRight className="w-5 h-5 text-green-600" />
                                    ) : (
                                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                                    )}
                                </button>
                            </div>

                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                                {type.description || "No description"}
                            </p>

                            <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-1 text-blue-600 font-semibold">
                                    <DollarSign className="w-4 h-4" />
                                    {parseFloat(type.price).toFixed(2)} USD
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openEditModal(type)}
                                        className="text-gray-500 hover:text-blue-600 p-1"
                                        title="Edit"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(type.id, type.name)}
                                        className="text-gray-500 hover:text-red-600 p-1"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Add / Edit Modal */}
            {(showAddModal || showEditModal) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-xl p-6 w-full max-w-md"
                    >
                        <h3 className="text-xl font-semibold text-gray-900 mb-5">
                            {showEditModal ? "Edit Case Type" : "Add New Case Type"}
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Immigration Visa Application"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    rows="3"
                                    placeholder="Complete visa application processing..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD) *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="500.00"
                                    required
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded"
                                />
                                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                                    Active (visible to clients)
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setShowEditModal(false);
                                        resetForm();
                                    }}
                                    disabled={saving}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-70"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Plus className="w-4 h-4" />
                                            {showEditModal ? "Update" : "Add"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default CaseTypes;